import type { ResponseCursors } from '@/types';
import type { PaginationModel } from '@montugroup/design-system';
import { useState } from 'react';
import { useGetPatientPrescriptions } from './useGetPatientPrescriptions';

interface Prescription {
  Doctor: Doctor;
  id: number | null;
  url: string | null;
  active: boolean | null;
  consultation_id?: number | null;
  created_date: string;
  doctor_id: number | null;
  order_code?: string | null;
  order_date?: string | null;
  patient_id?: number | null;
  PrescriptionStatus: PrescriptionStatus;
  PrescribedProducts?: PrescribedProduct[];
}

interface Doctor {
  DoctorUser: DoctorUser;
  id: number | null;
}

interface DoctorUser {
  id?: number | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface PrescriptionStatus {
  name: string | null;
}

interface PrescribedProduct {
  active: boolean | null;
  created_date: string | null;
  dosage?: string | null;
  id: number;
  other_product?: string | null;
  prescription_id: number;
  prescription_product_code?: string | null;
  product_id?: number | null;
  quantity?: number | null;
  repeats?: number | null;
  interval?: string | null;
  Product: Product;
  RefillPrescribedProducts?: RefillPrescribedProduct[];
}

interface Product {
  formulation_id?: number | null;
  strain_id?: number | null;
  name: string | null;
  thc_strength_for_display?: string | null;
  cbd_strength_for_display?: string | null;
}

interface RefillPrescribedProduct {
  id: number | null;
  active: boolean | null;
  quantity?: number | null;
  interval?: number | null;
  repeats?: number | null;
  created_date: string | null;
  refill_prescription_product_code?: string | null;
  refill_prescription_id?: number | null;
  RefillPrescription?: RefillPrescription;
}

interface RefillPrescription {
  id?: number | null;
  refill_prescription_code?: string | null;
  refill_prescription_url?: string | null;
  active?: boolean | null;
  created_date?: string | null;
}

export const usePatientPrescriptions = (patientId: number) => {
  const [prescriptionsCursors, setPrescriptionsCursors] = useState<ResponseCursors>({ prev: null, next: null });
  const [prescriptionsSearch, setPrescriptionsSearch] = useState<string>('');
  const prescriptionsPageParams = {
    size: 20,
    after: prescriptionsCursors?.next ?? undefined,
    before: prescriptionsCursors?.prev ?? undefined,
  };

  const {
    data: patientPrescriptions,
    isLoading: isPatientPrescriptionsLoading,
    refetch: refetchPrescriptions,
    isError: isPatientPrescriptionsError,
  } = useGetPatientPrescriptions(prescriptionsPageParams, prescriptionsSearch, patientId);

  const responsePrescriptionsCursors = patientPrescriptions?.meta?.page?.cursor ?? { prev: null, next: null };

  const patientPrescriptionsData: Prescription[] | undefined = patientPrescriptions
    ? patientPrescriptions?.data?.map((prescription) => ({
        Doctor: {
          DoctorUser: {
            id: prescription.attributes.doctor?.userId,
            last_name: prescription.attributes.doctor?.lastName,
            first_name: prescription.attributes.doctor?.firstName,
          },
          id: prescription.attributes.doctorId,
        },
        id: prescription.id,
        url: prescription.attributes.url,
        active: prescription.attributes.active,
        consultation_id: prescription.attributes.consultationId,
        created_date: prescription.attributes.createdDate,
        doctor_id: prescription.attributes.doctorId,
        order_code: prescription.attributes.orderCode,
        order_date: prescription.attributes.orderDate,
        patient_id: prescription.attributes.patientId,
        PrescriptionStatus: {
          name: prescription.attributes.status,
        },
        PrescribedProducts: prescription?.attributes.prescribedProducts?.map((product) => ({
          active: product.active,
          created_date: product.createdDate,
          dosage: product.dosage,
          id: product.id,
          other_product: product.otherProduct,
          prescription_id: prescription.id,
          prescription_product_code: product.prescriptionProductCode,
          product_id: product.productId,
          quantity: product.quantity,
          repeats: product.repeats,
          interval: product.interval,
          Product: {
            formulation_id: product.formulationId,
            strain_id: product.strainId,
            name: product.name,
            thc_strength_for_display: product.thcStrengthForDisplay,
            cbd_strength_for_display: product.cbdStrengthForDisplay,
          },
          RefillPrescribedProducts: product.refillPrescribedProducts?.map((refill) => ({
            id: refill.id,
            active: refill.active,
            quantity: refill.quantity,
            interval: refill.interval,
            repeats: refill.repeats,
            created_date: refill.createdDate,
            refill_prescription_product_code: refill.refillPrescriptionProductCode,
            refill_prescription_id: refill.refillPrescription?.id,
            RefillPrescription: {
              id: refill.refillPrescription?.id,
              refill_prescription_code: refill.refillPrescription?.refillPrescriptionCode,
              refill_prescription_url: refill.refillPrescription?.refillPrescriptionUrl,
              active: refill.refillPrescription?.active,
              created_date: refill.refillPrescription?.createdDate,
            },
          })),
        })),
      }))
    : undefined;

  const handlePrescriptionsPaginationChange = ({ page: isNewPage }: PaginationModel) => {
    const cursorKey = isNewPage > 0 ? 'next' : 'prev';
    if (responsePrescriptionsCursors?.[cursorKey]) {
      if (cursorKey === 'next') {
        setPrescriptionsCursors({ prev: null, next: responsePrescriptionsCursors[cursorKey] });
      } else {
        setPrescriptionsCursors({ prev: responsePrescriptionsCursors[cursorKey], next: null });
      }
    }
  };

  return {
    isPatientPrescriptionsLoading,
    responsePrescriptionsCursors,
    handlePrescriptionsPaginationChange,
    patientPrescriptionsData,
    setPrescriptionsCursors,
    refetchPrescriptions,
    setPrescriptionsSearch,
    isPatientPrescriptionsError,
  };
};
